import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import ArticleCard from '@/components/ArticleCard';
import { BookmarkCheck, Loader2 } from 'lucide-react';

function ProfilePage({ user, token }) {
  const navigate = useNavigate();
  const [savedArticles, setSavedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedArticleIds, setSavedArticleIds] = useState([]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    loadSavedArticles();
  }, [token, navigate]);

  const loadSavedArticles = async () => {
    try {
      const [articlesResponse, idsResponse] = await Promise.all([
        axios.get(`${API}/user/saved-articles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/user/saved-article-ids`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSavedArticles(articlesResponse.data);
      setSavedArticleIds(idsResponse.data);
    } catch (error) {
      console.error('Error loading saved articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const onToggleSave = (articleId, isSaved) => {
    if (!isSaved) {
      // Remove from list
      setSavedArticles(prev => prev.filter(a => a.id !== articleId));
      setSavedArticleIds(prev => prev.filter(id => id !== articleId));
    }
  };

  const onPromote = (articleId) => {
    // Update the article's promoted status in the local state
    setSavedArticles(prev => prev.map(article => 
      article.id === articleId 
        ? { ...article, promoted: new Date().toISOString() }
        : article
    ));
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen" data-testid="profile-page">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">{user.name}</h1>
              <p className="text-lg opacity-90">{user.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Articles Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <BookmarkCheck className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">Saved Articles</h2>
          <span className="text-muted-foreground">({savedArticles.length})</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : savedArticles.length === 0 ? (
          <div className="text-center py-12">
            <BookmarkCheck className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No saved articles yet</h3>
            <p className="text-muted-foreground mb-4">
              Start saving articles to read them later!
            </p>
            <Link 
              to="/"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              data-testid="browse-articles-link"
            >
              Browse Articles
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {savedArticles.map(article => (
              <ArticleCard
                key={article.id}
                article={article}
                token={token}
                user={user}
                savedArticleIds={savedArticleIds}
                onToggleSave={onToggleSave}
                onPromote={onPromote}
                openAuthModal={() => {}}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;